import type { Directive, DirectiveBinding } from 'vue'
import { watch } from 'vue'
import { useAuthStore } from '@/modules/auth'
import { hasPermission } from '@/core/composables/usePermissions'

function applyPermission(
  el: HTMLElement,
  binding: DirectiveBinding<string | string[] | undefined>,
) {
  const value = binding.value
  if (value == null || value === '') {
    el.style.removeProperty('display')
    return
  }

  const ok = hasPermission({
    permission: value,
    permissions_key: 'permissions',
  })

  el.style.display = ok ? '' : 'none'
}

/**
 * Oculta el elemento si el usuario no tiene el permiso.
 * - `v-permission="'users.invite'"`
 * - `v-permission="['a','b']"` → ANY (OR), igual que `hasPermission` por defecto.
 * - Sin valor / vacío: no oculta.
 */
export const permissionDirective: Directive<
  HTMLElement,
  string | string[] | undefined
> = {
  mounted(el, binding) {
    const ctx = { binding }
    applyPermission(el, binding)
    const auth = useAuthStore()
    const stop = watch(
      () => [auth.user?.permissions, auth.user?.ulid] as const,
      () => applyPermission(el, ctx.binding),
      { deep: true },
    )
    ;(
      el as HTMLElement & { __permStop?: () => void; __permCtx?: typeof ctx }
    ).__permStop = stop
    ;(
      el as HTMLElement & { __permStop?: () => void; __permCtx?: typeof ctx }
    ).__permCtx = ctx
  },
  updated(el, binding) {
    const ctx = (
      el as HTMLElement & { __permCtx?: { binding: typeof binding } }
    ).__permCtx
    if (ctx) ctx.binding = binding
    applyPermission(el, binding)
  },
  unmounted(el) {
    ;(el as HTMLElement & { __permStop?: () => void }).__permStop?.()
  },
}
